import {Component, OnInit, ViewEncapsulation, Input, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy} from '@angular/core';
import { OrderService } from '../../../services/order.service';
import { Order } from '../../../models/order.model';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { observeOn, takeWhile } from 'rxjs/operators';
import { asapScheduler } from 'rxjs';
import { ECOMMERCE_TYPE, PREORDER_TYPE } from '../../../constants/order-types';
import {ModalService} from '../../../services/modal.service';
import {isPaymentMethodDefined} from '../../../shared/helpers';
import {ConfigService} from '../../../services/config.service';
import {isEmptyArray} from '../../../shared/utils';
import {AddressService} from '../../../services/address.service';


@Component({
    selector: 'app-cart-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class CartSelectComponent  implements OnInit, OnDestroy {


    @Input() mobile = false;
    @Input() allowAdd: boolean;

    @Input() withOrderWindowTracking = false;

    @Output() selectedOrder = new EventEmitter<Order>();

    private isNewOrder = false;
    private alive = true;
    // orderService.currentOrders allocates a new array on every read; ng-select rebuilds its
    // option list (and the option DOM) whenever the [items] reference changes, which kills the
    // click on an option. Hand it a reference that only changes when the orders really change.
    private ordersCache: Order[] = [];
    private initialized = false;
    private creatingOrder = false;

    // private orders$: Observable<Order[]>;

    constructor(
        protected orderService: OrderService,
        protected toastr: ToastrService,
        private authService: AuthService,
        private modalService: ModalService,
        private configService: ConfigService,
        private addressService: AddressService
    ) {

    }

    ngOnInit() {
        this.init();
    }


    private init() {

        // All three streams are ComponentStore selects: they replay synchronously on subscribe, i.e.
        // inside the change-detection pass that instantiates this component. selectOrder() writes to
        // shared order state (switchActiveOrder, or createFakeOrder when the user has no cart yet),
        // and mutating it mid-pass leaves already-checked bindings elsewhere stale — the header's
        // on-demand cart summary then throws NG0100. observeOn(asapScheduler) delivers on a
        // microtask instead, so the write lands after the pass and Angular picks it up on the next
        // tick, still before paint. takeWhile stays after it so destroy is checked at delivery time.
        this.orderService.isInitialized().pipe(
            observeOn(asapScheduler),
            takeWhile(() => this.alive)
        ).subscribe( result => {
            this.initialized = result;
            if (this.initialized && !this.withOrderWindowTracking) {
                this.selectOrder();
            }


        });

        if (this.withOrderWindowTracking) {
            this.orderService.buyingWindowChanged.pipe(
                observeOn(asapScheduler),
                takeWhile(() => this.alive)
            ).subscribe(() => {

               if (this.orderService.currentOrderType === PREORDER_TYPE) {
                   this.selectOrder();
               }

            });

            this.orderService.orderTypeChanged.pipe(
                observeOn(asapScheduler),
                takeWhile(() => this.alive)
            ).subscribe(() => {
                this.selectOrder();
            })
        }
    }
    ngOnDestroy(): void {
        this.alive = false;
    }


    selectOrder(order?: Order) {
        if (!this.initialized) {
            return;
        }
        // user selected order
        if (order) {
            this.orderService.switchActiveOrder(order).subscribe( retOrder => {
                this.selectedOrder.emit(retOrder);
                // this.addressService.setAddressesByOrderType(retOrder.findAllOrderAddresses(), this.orderType);
            });
        } else if (!this.orderService.activeOrder) {
            if (this.orderService.currentOrders.length === 0 ) {
                // this.addOrder();
                this.createFakeOrder();
            } else {
                this.orderService.switchActiveOrder(this.orderService.currentOrders[0]).subscribe( retOrder => {
                    this.selectedOrder.emit(retOrder)
                });
            }

            // this.addressService.setAddressesByOrderType([], this.orderType);
        }
    }


    createFakeOrder() {
        const order  = this.orderService.createFakeOrder(this.orderType);
        this.selectedOrder.emit(order);
    }


    addOrder() {
        if (!this.allowAdd || this.creatingOrder || !this.orderType) {
            return;
        }

        this.creatingOrder = true;
        this.modalService.createOrderWithSettings({orderType: this.orderType}).subscribe( retOrder => {
            this.creatingOrder = false;
            if (retOrder) {
                this.selectedOrder.emit(retOrder);
            }
        });
    }


    get label(): string {
        return this.orderType === ECOMMERCE_TYPE  ? 'CURRENT CART' : 'CURRENT ORDER';
    }

    get buttonLabel(): string {
        return this.orderType === ECOMMERCE_TYPE  ? 'CREATE NEW CART' : 'CREATE NEW ORDER';
    }

    get orders(): Order[] {
        const orders = this.orderService.currentOrders;
        const unchanged = orders.length === this.ordersCache.length
            && orders.every((order, i) => order === this.ordersCache[i]);

        if (!unchanged) {
            this.ordersCache = orders;
        }

        return this.ordersCache;
    }

    get orderType(): string {
        return this.orderService.currentOrderType;
    }

    getOrderLabel(item: Order) {
        return item.selectLabel;
    }


    get visible(): boolean {
        return this.orderType
            && this.canOrder
            && !( this.orderType === PREORDER_TYPE && this.orderService.activeOrderWindow?.active !== true)
    }


    get activeOrder(): Order {
        return this.orderService.activeOrder;
    }

    get selectedID(): number {
        return this.activeOrder ? this.activeOrder.id : 0;
    }

    // should be present for ng-model binding
    set selectedID(val) {
        // setter required by [(ngModel)]
    }

    isPaymentDefined(order: Order) {
        return isPaymentMethodDefined(order, this.authService.user,
            this.authService.canShowSAP && this.configService.showBillingInfo) === true;
    }

    get canOrder(): boolean {
        return this.authService.canOrder;
    }

    get canChangeOrderSettings() {
        return this.authService.canEditOrder(this.activeOrder);
    }


    get hasOrders(): boolean {
        return !isEmptyArray(this.orderService.currentOrders) && this.orderService.currentOrders.some( order => !order.isNotReal);
    }
}
